import * as React from "react";
import {
  Cell2Checkbox,
  Cell2Input,
  Cell2Number,
} from "src/components/Sheet2/components";
import { Checkbox } from "antd";
import { Mc2EntityStore } from "../Mc2EntityStore";
import { Mc2Selection, Mc2SelectionTitle } from "./components/Mc2Selection";
import { Mc2Column } from "./Mc2Column";
import { Mc2CellPoint } from "./components/Mc2CellPoint";
import { mc2Checkbox, mc2Input, mc2Number } from "./components/mc2ui";
import { ZMc2TableRow } from "../Mc2EntityTypes";
import { Mc2ReadonlyCell } from "./components/Mc2ReadonlyCell";

// key должен соответствовать названию поля из ZMc2TableRow
const mkKey = (k: keyof ZMc2TableRow): string => k;

type Field =
  | "description"
  | "point"
  | "scaleMinus"
  | "scalePlus"
  | "tolMinus"
  | "tolPlus";

const safe = (
  store: Mc2EntityStore,
  row: ZMc2TableRow,
  field: Field,
  editor: React.ReactNode,
): React.ReactNode =>
  store.readOnly ? <Mc2ReadonlyCell text={row[field]} /> : editor;

export const buildMc2Columns = (store: Mc2EntityStore): Mc2Column[] => [
  ...(store.readOnly
    ? []
    : [
        {
          key: "@selection",
          title: <Mc2SelectionTitle store={store} />,
          align: "center",
          titleAlign: "center",
          minWidth: 32,
          render: ({ id }) => <Mc2Selection store={store} rowId={id} />,
        } satisfies Mc2Column,
      ]),
  {
    key: mkKey("point"),
    title: "Point",
    minWidth: 70,
    width: "1fr",
    render: (row) =>
      safe(store, row, "point", <Mc2CellPoint store={store} row={row} />),
  },
  {
    key: mkKey("description"),
    title: "Description",
    width: "2fr",
    minWidth: 278,
    render: (row) =>
      safe(
        store,
        row,
        "description",
        <Cell2Input
          cell={store.mkCell("description", row, "")}
          editorProps={mc2Input}
        />,
      ),
  },
  {
    key: mkKey("criticality"),
    title: "QC",
    align: "center",
    minWidth: 48,
    render: (row) =>
      store.readOnly ? (
        <Checkbox checked={row.criticality} disabled />
      ) : (
        <Cell2Checkbox
          cell={store.mkCell("criticality", row, false)}
          editorProps={mc2Checkbox}
        />
      ),
  },
  {
    key: mkKey("scaleMinus"),
    title: "Scale -",
    minWidth: 70,
    width: "1fr",
    render: (row) =>
      safe(
        store,
        row,
        "scaleMinus",
        <Cell2Number
          cell={store.mkCell("scaleMinus", row, 0)}
          editorProps={mc2Number}
        />,
      ),
  },
  {
    key: mkKey("scalePlus"),
    title: "Scale +",
    minWidth: 70,
    width: "1fr",
    render: (row) =>
      safe(
        store,
        row,
        "scalePlus",
        <Cell2Number
          cell={store.mkCell("scalePlus", row, 0)}
          editorProps={mc2Number}
        />,
      ),
  },
  {
    key: mkKey("tolMinus"),
    title: "Tol -",
    minWidth: 70,
    render: (row) =>
      safe(
        store,
        row,
        "tolMinus",
        <Cell2Number
          cell={store.mkCell("tolMinus", row, 0)}
          editorProps={mc2Number}
        />,
      ),
  },
  {
    key: mkKey("tolPlus"),
    title: "Tol +",
    minWidth: 70,
    render: (row) =>
      safe(
        store,
        row,
        "tolPlus",
        <Cell2Number
          cell={store.mkCell("tolPlus", row, 0)}
          editorProps={mc2Number}
        />,
      ),
  },
  ...store.table.header.map(
    (size) =>
      ({
        key: sizeColumnKey(size),
        title: size,
        minWidth: 80,
        width: "1fr",
        render: (row) =>
          store.readOnly ? (
            <Mc2ReadonlyCell text={row.sizes[size]?.value} />
          ) : (
            <Cell2Number
              cell={store.mkSizeCell(size, row)}
              editorProps={mc2Number}
            />
          ),
      }) satisfies Mc2Column,
  ),
];

export const sizeColumnKey = (size: string | null | undefined) =>
  `size:${size}`;
